/**
 * DEPENDENCY-FREE BY DESIGN — this module imports nothing.
 *
 * The volatility vocabulary and its proposal rules are needed on BOTH sides: the
 * engine applies them, and the dashboard must SHOW the proposal before the owner
 * saves (the recorded decision: propose and override, never a silent default).
 *
 * `VolatilityClass` therefore lives HERE and workers/facts.ts imports it from
 * this module, rather than the reverse. That direction matters: facts.ts carries
 * D1Database in its type surface, so importing the type from there would drag
 * worker-only types into the frontend's tsc graph and break the Pages build.
 * This is the same extraction, for the same reason, as workers/sentinel.ts —
 * which was pulled out of capture.ts after exactly that failure.
 */
export type VolatilityClass = 'short' | 'medium' | 'long' | 'cert_tied'


/**
 * Propose how fast a fact will go stale, from what the owner actually said.
 *
 * ==== WHY THIS IS RULES AND NOT AN LLM CALL ====
 * The owner can change this in one tap on the approve screen. Spending a
 * metered, non-deterministic model call on a decision that a human is about to
 * confirm anyway would be gold-plating — and worse, it would put variance
 * somewhere no test can pin down. The same transcript must produce the same
 * proposal every time, forever, or the approve screen becomes unpredictable for
 * no benefit. Rules are free, instant, and testable.
 *
 * ==== WHY THE PROPOSAL MUST BE VISIBLE, NOT SILENT ====
 * This function proposes; workers/api.ts returns the proposal with the draft so
 * the approve screen can SHOW it. That is the whole difference between
 * "propose and override" and a hidden default wearing a friendlier name. The
 * failure mode of a silent default is invisible: a price quietly classed as
 * long-lived answers stale for a year and nothing ever flags it. Shown, the
 * owner corrects it in a tap; hidden, nobody ever finds out.
 *
 * ==== WHY THE FALLBACK IS `medium` AND NOT `short` ====
 * Erring short LOOKS safer — stale facts are exactly what the decay machinery
 * exists to prevent. But a `short` default means asking the owner to re-confirm
 * coverage and process facts every couple of months, and the build document is
 * explicit that owner reply rates are the untested assumption underneath all of
 * Build 3. An owner trained to ignore the weekly message breaks the entire decay
 * feature; one fact drifting with an expiry flag already on it does not. So the
 * unsure case sits in the middle, where being wrong is cheap in both directions.
 */

/**
 * Certificate and qualification language. Checked FIRST and separately, because
 * cert_tied is the one class the engine cannot derive a duration for — the
 * certificate's own expiry date is the expiry — so a fact that lands here needs
 * a date from the owner rather than a default. Getting this wrong in either
 * direction has the sharpest consequence in the product: an expired
 * qualification answering as current is the one staleness with a liability
 * attached.
 */
const CERT_PATTERNS: readonly RegExp[] = [
  /\b(gas safe|niceic|napit|elecsa|stroma|oftec|hetas|corgi)\b/i,
  /\b(certificat\w*|accredit\w*|registrat\w+ number)\b/i,
  /\b(qualified|qualification|city\s*(&|and)\s*guilds|nvq|rpii|cscs)\b/i,
  /\b(insurance|indemnity|public liability|employers liability)\b/i,
  /\b(dbs|checked|vetted)\s+(check|certificate|clear)/i,
]

/**
 * Prices, quotes, charges and lead times — the things that move fastest and are
 * most damaging when stale, because a customer reads a number as a commitment.
 */
const SHORT_PATTERNS: readonly RegExp[] = [
  /[£$€]\s?\d/,
  /\b\d+\s*(pounds?|quid|p\/h|per hour|an hour|a day|per day)\b/i,
  /\b(price[sd]?|pricing|cost[s]?|charge[s]?|rate[s]?|fee[s]?|quote[sd]?)\b/i,
  /\b(callout|call-out|minimum charge|deposit|discount|vat)\b/i,
  /\b(lead time|turnaround|how (long|soon)|within \w+ (day|week)s?)\b/i,
  /\b(currently|at the moment|right now|this (week|month)|special offer)\b/i,
]

/**
 * Availability, staffing, capacity, current workload — real but slower-moving.
 */
const MEDIUM_PATTERNS: readonly RegExp[] = [
  /\b(availab\w+|booked up|busy|capacity|waiting list|backlog)\b/i,
  /\b(staff|team|engineers?|fitters?|lads|apprentice|subcontract\w*)\b/i,
  /\b(opening (hours|times)|open (on|at)|working hours|out of hours|emergency cover)\b/i,
  /\b(onboarding|set ?up time|installation time|takes about|usually takes)\b/i,
  /\b(hiring|recruiting|taking on)\b/i,
]

/**
 * How the business works and where its edges are — process, coverage, policy,
 * boundaries. These are the facts that are true for years, and the ones the
 * refusal behaviour depends on most (Build 4 triages against them).
 */
const LONG_PATTERNS: readonly RegExp[] = [
  /\b(we (don'?t|do not|never|only|always)\b)/i,
  /\b(cover|covering|coverage|area|radius|miles|within \d+\s*miles|travel)\b/i,
  /\b(guarantee|warranty|policy|process|procedure|the way we|how we)\b/i,
  /\b(established|founded|family|years? in (business|the trade)|since \d{4})\b/i,
  /\b(residential|commercial|domestic|new build|listed building)\b/i,
  /\b(payment terms|invoice|bacs|card|cash|terms and conditions)\b/i,
]

/**
 * THE BUSINESS'S OWN REFERENCE NUMBERS — VAT number, company number, UTR,
 * EORI, bank details. Kelvo A20, 29 Sep 2026: a document fact reading "VAT number GB 123
 * 4567 89" was proposed as `short`, because the price list matches a bare
 * "vat", and "company registration number" would have been proposed as
 * `cert_tied` (asking the owner for a certificate expiry date that does not
 * exist), because the certificate list matches "registration number".
 *
 * An identifier is issued once and changes only when the business itself
 * changes, so it is `long`. But it must never shield a price or a certificate
 * that shares its sentence: "£75 call-out plus VAT, VAT number GB…" is still
 * a price fact, and the faster-moving class must still win (see the ORDER
 * rule on proposeVolatility). So identifier phrases are REMOVED from the text
 * first and the ordinary rules run on what remains; `long` applies only when
 * nothing else is left to match.
 *
 * NARROW ON PURPOSE. Only the business's own registration identifiers. A card
 * or certificate number ("CSCS card number", "Gas Safe registration number")
 * is NOT here: those expire, and the certificate rule must keep them. Each
 * narrowing has a near-miss test in tests/volatility-identifiers.test.ts.
 */
const IDENTIFIER_PATTERNS: readonly RegExp[] = [
  /\bvat\s+(registration\s+|reg\.?\s+)?(number|no\.?)(?=\W|$)/gi,
  /\bcompany\s+(registration\s+|reg\.?\s+)?(number|no\.?)(?=\W|$)/gi,
  /\bregistered\s+company\s+(number|no\.?)(?=\W|$)/gi,
  /\bcompanies\s+house\s+(registration\s+)?(number|no\.?)(?=\W|$)/gi,
  /\bregistered\s+in\s+(england(\s+(and|&)\s+wales)?|wales|scotland|northern\s+ireland)\b/gi,
  /\b(utr|unique\s+taxpayer\s+reference)(\s+(number|no\.?))?(?=\W|$)/gi,
  /\beori(\s+(number|no\.?))?(?=\W|$)/gi,
  // Bank details: fixed until the business changes bank. "swift" only as
  // "swift code" — "a swift reply" is not a reference number.
  /\bsort\s+code\b/gi,
  /\b(bank\s+)?account\s+(number|no\.?)(?=\W|$)/gi,
  /\biban\b/gi,
  /\b(bic|swift\s+code)\b/gi,
]

/**
 * The text with every identifier phrase blanked out, and whether there was one.
 * Blanked to a space rather than deleted, so the words either side cannot join
 * into a new word that a later rule matches.
 */
function withoutIdentifiers(text: string): { rest: string; found: boolean } {
  let rest = text
  let found = false
  for (const pattern of IDENTIFIER_PATTERNS) {
    rest = rest.replace(pattern, () => {
      found = true
      return ' '
    })
  }
  return { rest, found }
}

export interface VolatilityProposal {
  volatilityClass: VolatilityClass
  /**
   * Plain English, for the approve screen. The owner is a tradesperson, not a
   * data modeller: he should read a sentence about HIS facts, never a schema
   * enum. The words the engine uses internally never reach him.
   */
  label: string
  /** One line explaining the proposal, so an override is an informed one. */
  reason: string
  /**
   * True when no rule matched and this is the fallback rather than a positive
   * match. The approve screen can nudge slightly harder in that case — the
   * difference between "we think this is a price" and "we could not tell".
   */
  isFallback: boolean
}

/** Human wording for each class. Never shows the enum value itself. */
const LABELS: Record<VolatilityClass, string> = {
  short: 'Changes often — check every couple of months',
  medium: 'Changes now and then — check twice a year',
  long: 'Rarely changes — check once a year',
  cert_tied: 'Tied to a certificate — expires on its own date',
}

function matches(text: string, patterns: readonly RegExp[]): boolean {
  return patterns.some((p) => p.test(text))
}

/**
 * Propose a class for a transcript.
 *
 * ORDER IS THE RULE, and it is deliberate: certificates first (they need a date,
 * not a duration), then prices (most damaging when stale, and a sentence that
 * mentions a price IS a price fact even if it also mentions coverage), then
 * availability, then process. A sentence like "we charge £75 to come out
 * anywhere within twenty miles" is a PRICE fact that happens to mention an area
 * — classing it long because it says "miles" would let the price go stale for a
 * year. When in doubt about a mixed sentence, the faster-moving class wins,
 * because the cost of re-confirming too often is a message and the cost of
 * re-confirming too rarely is a wrong answer to a customer.
 */
export function proposeVolatility(transcript: string): VolatilityProposal {
  // Identifier phrases never count as evidence for any other class (A20).
  const { rest: text, found: identifier } = withoutIdentifiers(transcript.trim())

  if (matches(text, CERT_PATTERNS)) {
    return {
      volatilityClass: 'cert_tied',
      label: LABELS.cert_tied,
      reason: 'This mentions a qualification, certificate or insurance, so it should expire when that does.',
      isFallback: false,
    }
  }
  if (matches(text, SHORT_PATTERNS)) {
    return {
      volatilityClass: 'short',
      label: LABELS.short,
      reason: 'This mentions prices or timings, which tend to change the fastest.',
      isFallback: false,
    }
  }
  if (matches(text, MEDIUM_PATTERNS)) {
    return {
      volatilityClass: 'medium',
      label: LABELS.medium,
      reason: 'This is about availability or how you are set up, which shifts now and then.',
      isFallback: false,
    }
  }
  if (matches(text, LONG_PATTERNS)) {
    return {
      volatilityClass: 'long',
      label: LABELS.long,
      reason: 'This is about how you work or where you cover, which rarely changes.',
      isFallback: false,
    }
  }

  // Only a reference number was left to match: it rarely changes (A20).
  if (identifier) {
    return {
      volatilityClass: 'long',
      label: LABELS.long,
      reason: 'This is a reference number, like a VAT or company number, which rarely changes.',
      isFallback: false,
    }
  }

  // Nothing matched. Middle ground — see the header for why this is not `short`.
  return {
    volatilityClass: 'medium',
    label: LABELS.medium,
    reason: 'Not sure how often this changes, so we have put it in the middle. Change it if you know better.',
    isFallback: true,
  }
}

/** The four choices, for the override control. Exported so the UI cannot invent its own wording. */
export const VOLATILITY_CHOICES: ReadonlyArray<{ value: VolatilityClass; label: string }> = [
  { value: 'short', label: LABELS.short },
  { value: 'medium', label: LABELS.medium },
  { value: 'long', label: LABELS.long },
  { value: 'cert_tied', label: LABELS.cert_tied },
]
